import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, PageError, PageLoading } from "../components/index.js";
import { Field, Select } from "../components/AdminForm.jsx";
import { useApi, useApiAction } from "../hooks/useApi.js";

/**
 * Proposals (/agency/proposals) — Phase 4b-2. List with status chips, New
 * Proposal modal (title + prospect-or-client), row → builder.
 */
const FILTERS = [
  { key: "all", label: "All" }, { key: "draft", label: "Draft" }, { key: "sent", label: "Sent" },
  { key: "expired", label: "Expired" },
  { key: "accepted", label: "Accepted" }, { key: "rejected", label: "Rejected" },
];
const CHIP = {
  draft:    { bg: "#f5f5f5", fg: "#525252", label: "Draft" },
  sent:     { bg: "#eff6ff", fg: "#1d4ed8", label: "Sent" },
  accepted: { bg: "#f0fdf4", fg: "#15803d", label: "Accepted" },
  rejected: { bg: "#fef2f2", fg: "#b91c1c", label: "Rejected" },
  expired:  { bg: "#fffbeb", fg: "#b45309", label: "Expired" },
};
const fmtMoney = (v) => `$${Number(v ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export function ProposalStatusChip({ status }) {
  const c = CHIP[status] || CHIP.draft;
  return (
    <span className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium" style={{ backgroundColor: c.bg, color: c.fg }}>
      {c.label}
    </span>
  );
}

export function forLabel(p) {
  return p.client ? p.client.name : p.prospect ? `${p.prospect.name} (prospect)` : "—";
}

export function AgencyProposals() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi("/api/proposals");
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);

  const proposals = useMemo(() => data?.proposals || [], [data]);
  const filtered = useMemo(() => proposals.filter((p) => filter === "all" || p.status === filter), [proposals, filter]);
  const counts = useMemo(() => {
    const m = { all: proposals.length };
    for (const p of proposals) m[p.status] = (m[p.status] || 0) + 1;
    return m;
  }, [proposals]);

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading || !data) return <PageLoading />;

  return (
    <div className="max-w-[1200px] space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)}
              className={`h-7 px-2.5 rounded-card border text-[12px] font-medium transition-colors ${filter === f.key ? "bg-accent text-white border-accent" : "border-border-default text-text-secondary hover:text-text-primary"}`}>
              {f.label}{counts[f.key] ? ` · ${counts[f.key]}` : ""}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setAdding(true)} className="px-3 py-1.5 rounded-card bg-accent text-white text-[12px] font-medium">
          + New Proposal
        </button>
      </div>

      <Card className="p-0">
        <table className="w-full text-[12px] border-collapse" style={{ fontFeatureSettings: "'tnum'" }}>
          <thead>
            <tr className="text-left text-text-muted text-[11px] uppercase border-b border-border-light" style={{ letterSpacing: "0.5px" }}>
              <th className="px-4 py-2.5 font-medium">Title</th>
              <th className="px-4 py-2.5 font-medium">For</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Sent to</th>
              <th className="px-4 py-2.5 font-medium">Expires</th>
              <th className="px-4 py-2.5 font-medium text-right">Setup</th>
              <th className="px-4 py-2.5 font-medium text-right">Monthly</th>
              <th className="px-4 py-2.5 font-medium text-right">Lines</th>
              <th className="px-4 py-2.5 font-medium text-right">Updated</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-text-muted">No proposals{filter === "all" ? " yet" : ` in ${filter}`}</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} onClick={() => navigate(`/agency/proposals/${p.id}`)} className="border-b border-border-light cursor-pointer hover:bg-app-bg">
                <td className="px-4 py-2.5 font-medium text-text-primary">{p.title}</td>
                <td className="px-4 py-2.5 text-text-secondary">{forLabel(p)}</td>
                <td className="px-4 py-2.5"><ProposalStatusChip status={p.status} /></td>
                <td className="px-4 py-2.5 text-text-secondary">{p.sentToName || p.sentToEmail || "—"}</td>
                <td className="px-4 py-2.5 text-text-muted">{p.expiresAt && ["sent", "expired"].includes(p.status) ? new Date(p.expiresAt).toLocaleDateString(undefined, { timeZone: "UTC" }) : "—"}</td>
                <td className="px-4 py-2.5 text-right">{fmtMoney(p.totalSetupFees)}</td>
                <td className="px-4 py-2.5 text-right">{fmtMoney(p.totalMonthlyRecurring)}/mo</td>
                <td className="px-4 py-2.5 text-right">{p.lineItemCount ?? 0}</td>
                <td className="px-4 py-2.5 text-right text-text-muted">{new Date(p.updatedAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {adding && (
        <NewProposalModal onClose={() => setAdding(false)} onCreated={(id) => { setAdding(false); navigate(`/agency/proposals/${id}`); }} />
      )}
    </div>
  );
}

/**
 * Title + "for" picker. `initial` lets entry points (prospect drawer, cockpit)
 * preselect the target; exported so those pages reuse this modal.
 */
export function NewProposalModal({ onClose, onCreated, initial = {} }) {
  const api = useApiAction();
  const { data: prospectsData } = useApi("/api/prospects");
  const { data: clientsData } = useApi("/api/clients");
  const prospects = useMemo(() => (prospectsData?.prospects || []).filter((p) => p.stage !== "lost").slice().sort((a, b) => a.name.localeCompare(b.name)), [prospectsData]);
  const clients = useMemo(() => (clientsData?.clients || []).slice().sort((a, b) => a.name.localeCompare(b.name)), [clientsData]);
  const [kind, setKind] = useState(initial.clientId ? "client" : "prospect");
  const [form, setForm] = useState({ title: initial.title || "", prospectId: initial.prospectId || "", clientId: initial.clientId || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit() {
    const target = kind === "client" ? form.clientId : form.prospectId;
    if (!form.title.trim()) return setErr(new Error("Title is required"));
    if (!target) return setErr(new Error(`Pick a ${kind}`));
    setBusy(true); setErr(null);
    try {
      const body = { title: form.title.trim(), ...(kind === "client" ? { clientId: form.clientId } : { prospectId: form.prospectId }) };
      const r = await api("/api/proposals", { method: "POST", body: JSON.stringify(body) });
      onCreated(r.proposal.id);
    } catch (e) { setErr(e); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[480px] max-w-full bg-surface rounded-card shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-3">New Proposal</h2>
        {err && <p className="text-[12px] text-negative mb-2">{err.message}</p>}
        <div className="space-y-2.5">
          <Field label="Title" required value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} placeholder="e.g. Website + Social — Q4 2026" />
          {!initial.clientId && !initial.prospectId && (
            <Select label="This proposal is for a" value={kind} onChange={setKind} options={[{ value: "prospect", label: "Prospect" }, { value: "client", label: "Existing client" }]} />
          )}
          {kind === "prospect" ? (
            <Select label="Prospect" required value={form.prospectId} onChange={(v) => setForm((f) => ({ ...f, prospectId: v }))}
              options={[{ value: "", label: "Select a prospect…" }, ...prospects.map((p) => ({ value: p.id, label: p.name }))]} />
          ) : (
            <Select label="Client" required value={form.clientId} onChange={(v) => setForm((f) => ({ ...f, clientId: v }))}
              options={[{ value: "", label: "Select a client…" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]} />
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary">Cancel</button>
          <button type="button" disabled={busy} onClick={submit} className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">{busy ? "Creating…" : "Create & open"}</button>
        </div>
      </div>
    </div>
  );
}
